import {useEffect, useMemo, useState} from 'react'
import _ from 'lodash'
import {useMeasure} from 'react-use'
import {IEvent} from '@/features/labeling'
import {hslToHex} from '@/utils/colors'
import {lerp} from '@/utils/math'
import './style.css'

export interface IVideoRegionsProps {
  videoRef: React.RefObject<HTMLVideoElement | null>
  currentTime?: number
  predictions?: IEvent[]
  onValueClick: (value: IEvent) => void
}

export function VideoRegions(props: IVideoRegionsProps) {
  const {videoRef, currentTime, predictions, onValueClick} = props

  const timeMargin = 0.005

  const [regions, setRegions] = useState<IEvent[]>([])
  const [videoInfo, setVideoInfo] = useState({
    width: 0,
    height: 0,
    aspectRatio: 1,
  })

  const [regionsRef, regionsSize] = useMeasure<HTMLDivElement>()

  useEffect(() => {
    if (!videoRef.current) return
    videoRef.current.addEventListener('loadedmetadata', handleVideoDimensions)
    return () => {
      if (!videoRef.current) return
      videoRef.current.removeEventListener('loadedmetadata', handleVideoDimensions)
    }
  }, [])

  useEffect(() => {
    const res: IEvent[] = _.chain(predictions)
      .map((p) => {
        if (currentTime === undefined) return
        if (!p) return
        if (!p.rect) return
        if (!(currentTime >= p.start - timeMargin && currentTime <= (p.end || p.start + 5))) return
        return p
      })
      .compact()
      .value()
    setRegions((prev) => {
      if (_.isEqual(prev, res)) return prev
      return res
    })
  }, [predictions, currentTime])

  function handleVideoDimensions(this: HTMLVideoElement, _ev: Event) {
    const ratio = !this.videoHeight ? 1 : this.videoWidth / this.videoHeight
    setVideoInfo({
      width: this.videoWidth,
      height: this.videoHeight,
      aspectRatio: ratio,
    })
  }

  function handleOnValueClick(value: IEvent) {
    if (!onValueClick) return
    onValueClick(value)
  }

  function parseOrLerpRegionValue(
    event: IEvent,
    value: number | number[] | undefined,
    defaultValue: number = 0
  ): number {
    if (value === undefined) return defaultValue
    if (_.isNumber(value)) return value
    if (value.length > 1 && currentTime !== undefined) {
      const len = (event.end || event.start + 5) - event.start
      const t = (currentTime - event.start) / len
      // const tStep = len / value.length;
      const a = _.first(value) || defaultValue
      const b = _.last(value) || defaultValue
      return lerp(a, b, t)
    }
    return _.first(value) || defaultValue
  }

  const style: React.CSSProperties = useMemo(() => {
    return {
      aspectRatio: videoInfo.aspectRatio,
      left: '50%',
      top: '50%',
      marginLeft: `${-regionsSize.width / 2}px`,
      marginTop: `${-regionsSize.height / 2}px`,
    }
  }, [regionsSize, videoInfo])

  return (
    <div className='video-regions'>
      <div
        className='regions'
        ref={regionsRef}
        style={style}
      >
        {regions.map((r, i) => {
          if (currentTime === undefined) return
          if (!r.rect) return
          const x = parseOrLerpRegionValue(r, r.rect.x)
          const y = parseOrLerpRegionValue(r, r.rect.y)
          const w = parseOrLerpRegionValue(r, r.rect.w)
          const h = parseOrLerpRegionValue(r, r.rect.h)
          return (
            <div
              key={i}
              className='region'
              style={{
                top: `${y * 100}%`,
                left: `${x * 100}%`,
                width: `${w * 100}%`,
                height: `${h * 100}%`,
                ...(r.hue !== undefined ? {borderColor: hslToHex(r.hue)} : undefined),
              }}
            >
              <div
                className='title'
                onClick={() => handleOnValueClick(r)}
              >
                {r.type}
              </div>
              {r.text ?
                <div className='text'>{r.text}</div>
              : undefined}
            </div>
          )
        })}
      </div>
    </div>
  )
}
